import { PRACTICE_KINDS, LEAD_TIME_OPTIONS, emptyPracticeRow } from '../../helpers/intelligence/insightPractice'

/**
 * Het blok 'In de praktijk' op de inzichtkaart: randvoorwaarden en valkuilen
 * met hun herkomst, plus de doorlooptijd.
 *
 * Dit is de enige plek waar een praktijkbijdrage in het product landt. Een
 * bezoeker stuurt hem in op de publieke inzichtpagina van de marketingsite, wij
 * krijgen een mail, en wat vrijgegeven wordt typt een redacteur hier over. Pas
 * daarna serveert de publieke API het.
 *
 * De herkomst is geen vrij veld: hij volgt de keuze van de bijdrager. Mocht die
 * met naam, dan naam + organisatie; mocht het alleen anoniem, dan de sectorzin
 * ("Een organisatie in arbeidsparticipatie"); mocht het niet, dan hoort de
 * regel hier helemaal niet te staan.
 *
 * AI-CONTEXT: See docs/features/inzicht-praktijkbijdrage.md
 *
 * @param {object} props
 * @param {string} props.insightId - Id van het inzicht; maakt de veld-id's uniek als er meer kaarten op één pagina staan
 * @param {Array<import('../../helpers/intelligence/insightPractice').PracticeRow>} props.rows - De regels zoals ze nu in het formulier staan
 * @param {string} props.leadTime - De gekozen doorlooptijd-band; '' = niet ingevuld
 * @param {(rows: Array<import('../../helpers/intelligence/insightPractice').PracticeRow>) => void} props.onRowsChange - Krijgt de volledige nieuwe lijst
 * @param {(leadTime: string) => void} props.onLeadTimeChange - Krijgt de nieuwe band
 */
const InsightPracticeFields = ({ insightId, rows, leadTime, onRowsChange, onLeadTimeChange }) => {
    /**
     * @param {number} index - Welke regel verandert
     * @param {Partial<import('../../helpers/intelligence/insightPractice').PracticeRow>} patch
     */
    const wijzig = (index, patch) => {
        onRowsChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)))
    }

    return (
        <div className="intelligence-practice">
            <h4>In de praktijk</h4>
            <p className="intelligence-panel__hint">
                Wat organisaties moeten doen om dit effect te laten werken, uit gemodereerde
                praktijkbijdragen. Vul de bron precies zoals de bijdrager het toestond.
            </p>

            {rows.length === 0 && (
                <p className="intelligence-state">Nog geen praktijkregels bij dit inzicht.</p>
            )}

            {rows.map((row, index) => (
                // De index is hier de sleutel omdat een regel geen eigen id heeft
                // en de lijst alleen achteraan groeit of ergens uit verdwijnt.
                // eslint-disable-next-line react/no-array-index-key
                <div key={index} className="intelligence-practice__row">
                    <select
                        aria-label={`Soort regel ${index + 1}`}
                        className="intelligence-practice__kind"
                        value={row.kind}
                        onChange={(e) => {
                            // Eerst uitlezen, dan pas zetten.
                            // AI-CONTEXT: See docs/pitfalls.md#state-updater-reads-event-target-too-late
                            const waarde = e.target.value
                            wijzig(index, { kind: waarde })
                        }}
                    >
                        {PRACTICE_KINDS.map((soort) => (
                            <option key={soort.key} value={soort.key}>{soort.label}</option>
                        ))}
                    </select>

                    <textarea
                        aria-label={`Regel ${index + 1}`}
                        className="intelligence-practice__text"
                        rows={2}
                        value={row.text}
                        placeholder="Bijvoorbeeld: een vaste begeleider per deelnemer."
                        onChange={(e) => {
                            const waarde = e.target.value
                            wijzig(index, { text: waarde })
                        }}
                    />

                    <input
                        aria-label={`Bron van regel ${index + 1}`}
                        className="intelligence-practice__source"
                        type="text"
                        value={row.source}
                        placeholder="Naam, Organisatie — of: Een organisatie in arbeidsparticipatie"
                        onChange={(e) => {
                            const waarde = e.target.value
                            wijzig(index, { source: waarde })
                        }}
                    />

                    <button
                        type="button"
                        className="intelligence-practice__remove"
                        onClick={() => onRowsChange(rows.filter((_, i) => i !== index))}
                    >
                        Regel weghalen
                    </button>
                </div>
            ))}

            <div className="intelligence-toolbar">
                <button type="button" onClick={() => onRowsChange([...rows, emptyPracticeRow()])}>
                    Regel toevoegen
                </button>
            </div>

            {/* De doorlooptijd hoort bij een overgenomen bijdrage; zonder regel is
                het een veld zonder uitleg (besluit user 2026-09-30). Een eerder
                ingevulde band blijft wél zichtbaar, anders raakt hij onbereikbaar. */}
            {(rows.length > 0 || leadTime) && (
            <div className="intelligence-insight-section">
                <label htmlFor={`insight-leadtime-${insightId}`}>Doorlooptijd</label>
                {/* Gestuurd (`value`) en niet `defaultValue`: de lijst staat vast, maar
                    het document komt uit een snapshot die later kan binnenvallen.
                    AI-CONTEXT: See docs/pitfalls.md#ui-select-defaultvalue-loses-late-options */}
                <select
                    id={`insight-leadtime-${insightId}`}
                    value={leadTime}
                    onChange={(e) => {
                        const waarde = e.target.value
                        onLeadTimeChange(waarde)
                    }}
                >
                    {LEAD_TIME_OPTIONS.map((optie) => (
                        <option key={optie.key} value={optie.key}>{optie.label}</option>
                    ))}
                </select>
            </div>
            )}
        </div>
    )
}

export default InsightPracticeFields
